import { IChatEditorVM } from 'components/editors/chat/chat.interfaces';
import { ThreadContentTypes } from 'enums/comments/contentTypes';
import { wccModules } from 'enums/wccModules';
import attachmentsFactory from 'factories/attachments';
import { TextHelpers } from 'helpers/text';
import { BlobWrapper } from 'interfaces/blobWrapper';
import { inject, injectable } from 'inversify';
import { Observable, ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { IWCCStorageManager } from 'managers/iStorage';
import TopicContentPlaceholdersManager from 'managers/topic/contentPlaceholders';
import TopicMediaManager from 'managers/topic/media';
import { EffectsContainer } from 'mixins/withEffects';
import WCCAttachment from 'models/attachments/attachment';
import WCCImage from 'models/attachments/image';
import WCCVideo from 'models/attachments/video';
import { JSONTopicThread, TopicThread } from 'models/topicThread';
import WCCUpload from 'models/upload';
import { ServicesContext } from 'services/context';
import ThreadEditorVM from '../thread';
import { ThreadEditorVMConfig } from '../thread.interfaces';
import TextThreadEditorContentManager from './managers/content';
import './textThread.scss';

components
    .registerLocal('thread-editor-text-body', () => require('./textThreadBody'))
    .registerLocal('thread-editor-text-inline-footer', () => require('./textThreadInlineFooter'))
    .registerLocal('thread-editor-text-whisper-button', () => require('./whisperButton'));

export interface TextThreadEditorVMConfig extends ThreadEditorVMConfig {
    focus?: SubscribableOrNullableValue<boolean>
    title?: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class TextThreadEditorVM extends ThreadEditorVM<JSONTopicThread> {
    private placeholdersManager: Subscribable<TopicContentPlaceholdersManager | undefined>
    private contentManager: TextThreadEditorContentManager

    private hasMedia: Subscribable<boolean>

    errorsShown = ko.observable(false);
    isForcedTextMode = ko.observable(false);

    attachmentsManager: TopicMediaManager
    attachments: ObservableArray<WCCAttachment>
    images: Subscribable<Array<WCCImage>>
    videos: Subscribable<Array<WCCVideo>>
    uploads: Subscribable<Array<WCCUpload>>

    titleEditor = ko.observable<IChatEditorVM>();
    titlePlaceholder: Subscribable<string>
    title: Observable<string>

    textEditor = ko.observable<IChatEditorVM>();
    textPlaceholder: Subscribable<string>
    text: Observable<string>    
    
    isFocusedOnRender: Subscribable<boolean>

    titleAllowed: Subscribable<boolean>
    textIsAllowed: Subscribable<boolean>
    imagesAreAllowed: Subscribable<boolean>
    filesAreAllowed: Subscribable<boolean>
    videoIsAllowed: Subscribable<boolean>
    audioIsAllowed: Subscribable<boolean>
    noMediaAllowed: Subscribable<boolean>
    mediaIsRequired: Subscribable<boolean>

    textMode: Subscribable<boolean>
    imagesMode: Subscribable<boolean>
    filesMode: Subscribable<boolean>
    videoMode: Subscribable<boolean>

    allowedFilesExtensions: Subscribable<Array<string>>

    isTitleEditorNeeded = ko.pureComputed(() => this.checkIfTitleEditorNeeded())
    isCaptionEditorNeeded = ko.pureComputed(() => this.checkIfCaptionEditorNeeded())
    isVoxPopMessageNeeded = ko.pureComputed(() => this.checkIfVoxPopMessageNeeded())
    isMaterialUIShowing = ko.pureComputed(() => this.materialUIEnabled());

    constructor(
        @inject(wccModules.componentConfig) config: TextThreadEditorVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.servicesContext) ctx: ServicesContext,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        super(config, info, storage, ctx, effects);

        this.isFocusedOnRender = ko.flattenComputed(config.focus, false);

        const initialTitle = this.thread.pluck(th => th.title, '');
        const titleAllowed = ko.flattenComputed(config.title, false);
        this.titleAllowed = ko.pureComputed(() => titleAllowed() || initialTitle().length > 0);

        this.placeholdersManager = storage.get(TopicContentPlaceholdersManager, { topicId: this.topicId });

        this.contentManager = new TextThreadEditorContentManager(this.topic, this.user, this.threadOwner, this.isReplyEditor, this.isForcedTextMode);
        this.attachmentsManager = effects.register(new TopicMediaManager({ topic: this.topic }));

        this.attachments = this.attachmentsManager.attachments;
        this.images = this.attachmentsManager.images;
        this.videos = this.attachmentsManager.videos;
        this.uploads = this.attachmentsManager.uploads;

        this.titlePlaceholder = this.placeholdersManager.pluck(m => m.titlePlaceholder, '');
        this.title = ko.pureComputed({
            read: () => this.titleEditor()?.content() ?? '',
            write: value => this.titleEditor()?.setContent(value, false)
        }).extend({ validate: () => this.validateTitle() });

        this.textPlaceholder = this.placeholdersManager.pluck(m => this.isReplyEditor() ? m.replyPlaceholder() : m.commentPlaceholder(), '');
        this.text = ko.pureComputed({
            read: () => this.textEditor()?.content() ?? '',
            write: value => this.textEditor()?.setContent(value, false)
        }).extend({ validate: () => this.validateText() });
        
        this.textIsAllowed = this.contentManager.textIsAllowed;
        this.imagesAreAllowed = this.contentManager.imagesAreAllowed;
        this.filesAreAllowed = this.contentManager.filesAreAllowed;
        this.videoIsAllowed = this.contentManager.videoIsAllowed;
        this.audioIsAllowed = this.contentManager.audioIsAllowed;
        this.noMediaAllowed = ko.pureComputed(() => !this.imagesAreAllowed() && !this.filesAreAllowed() && !this.videoIsAllowed() && !this.audioIsAllowed());
        this.mediaIsRequired = this.contentManager.mediaIsRequired;

        this.textMode = ko.pureComputed(() => this.textIsAllowed());
        this.imagesMode = ko.pureComputed(() => !this.textMode() && this.imagesAreAllowed());
        this.filesMode = ko.pureComputed(() => !this.textMode() && !this.imagesMode() && this.filesAreAllowed());
        this.videoMode = ko.pureComputed(() => !this.textMode() && !this.imagesMode() && !this.filesMode() && this.videoIsAllowed());

        this.allowedFilesExtensions = this.topic.pluck(topic => topic.allowedAttachmentExtensions, []);

        const isUserAModerator = this.threadOwner.pluck('isModerator', false);
        const isUserAnObserver = this.threadOwner.pluck('isObserver', false);

        this.hasMedia = ko.pureComputed(() => this.checkIfHasMedia());

        ko.applyBindingsToNode(info.element, {
            'css': {
                'moderator': isUserAModerator,
                'observer': isUserAnObserver
            }
        }, this);
    }    

    pickContent = (type: ThreadContentTypes) => {
        this.onPickContent(type);
    }

    pickImage = () => {
        return this.attachmentsManager.pickImage();
    }

    pickFiles = () => {
        return this.attachmentsManager.pickFiles();
    }

    pickVideo = () => {
        return this.attachmentsManager.pickVideo();
    }

    pickAudio = () => {
        return this.attachmentsManager.pickAudio();
    }

    uploadImage = (fileOrBlob: File | BlobWrapper) => {
        return this.attachmentsManager.uploadImage(fileOrBlob);
    }

    uploadFile = (fileOrBlob: File | BlobWrapper) => {
        return this.attachmentsManager.uploadFile(fileOrBlob);
    }

    uploadVideo = (fileOrBlob: File | BlobWrapper) => {
        return this.attachmentsManager.uploadVideo(fileOrBlob);
    }

    removeAttachment = (attachment: WCCAttachment) => {
        this.attachmentsManager.removeAttachment(attachment);
    }

    protected showText() { };

    protected onPickContent(type: ThreadContentTypes) {
        switch (type) {
            case 'text':
                this.showText();
                break;
            default:
                this.attachmentsManager.pick(type);
        }
    }

    protected validate() {
        this.errorsShown(false);
        this.errorsShown(true);

        return [
            this.validateTitle(),
            this.validateText(),
            this.validateMedia()
        ].reduce((result, error) => result ?? error, undefined);
    }

    protected override checkIfReady() {
        return super.checkIfReady() &&
            (this.placeholdersManager()?.ready() ?? false) &&
            (!this.isTitleEditorNeeded() || this.titleEditor()?.isReady() || false) &&
            this.attachmentsManager.isReady() &&
            (!this.textIsAllowed() || this.textEditor()?.isReady() || false);
    }

    protected override checkIfBusy() {
        return this.uploads().length > 0;
    }

    protected override checkIfHasChanges() {
        return super.checkIfHasChanges() ||
            this.title().length > 0 ||
            this.text().length > 0 ||
            this.attachments().length > 0 ||
            this.uploads().length > 0 ||
            this.isForcedTextMode();
    }

    protected checkIfTextIsMandatory() {
        return false;
    }

    protected override reset() {
        super.reset();

        this.title('');
        this.text('');
        this.attachments([]);
        this.isForcedTextMode(false);
        this.errorsShown(false);
    }

    protected getSaveData() {
        return this.getState();
    }

    protected getState() {
        const topic = this.topic();

        const state: JSONTopicThread = {
            Title: this.title(),
            ThreadContent: this.text(),
            Attachments: this.attachments().map(a => a.toJson())
        }

        if (topic != undefined) {
            if (topic.isVoxPop())
                state['ThreadType'] = this.videoMode() ? enums.ThreadTypes.Video.value : enums.ThreadTypes.Text.value;
        }

        return state;
    }

    protected setState(state: JSONTopicThread) {
        const topic = this.topic();

        const attachments = (state.Attachments ?? []).map(a => attachmentsFactory.createAttachment(a));
        const text = state.ThreadContent ?? '';
        const title = state.Title ?? '';

        if (topic?.isVoxPop())
            this.isForcedTextMode(text.length > 0);

        //We might just enabled text so we need to wait for editor to get rendered
        if (this.textIsAllowed()) {
            this.textEditor.whenNotNull().then(editor => editor.isReady.when()).then(() => this.text(text));

            if (this.isTitleEditorNeeded())
                this.titleEditor.whenNotNull().then(editor => editor.isReady.when()).then(() => this.title(title));
        }
        
        this.attachments(attachments);        
    }

    protected getStateFromThread(thread: TopicThread) {
        return thread.toJson();
    }

    private validateTitle() {
        if (this.isTitleEditorNeeded() && this.title().trim().length === 0)
            return messages.TitleCantBeEmpty;
    }

    private validateText() {
        if (this.textIsAllowed() && this.text().trim().length === 0 && (this.checkIfTextIsMandatory() || !this.hasMedia()))
            return messages.MessageCantBeEmpty;
    }

    private validateMedia() {
        const topic = this.topic();
        const threadOwner = this.threadOwner();

        if (topic != undefined && threadOwner != undefined) {
            if (this.mediaIsRequired()) {
                var hasMedia = this.hasMedia();

                if (!hasMedia)
                    return messages.YouMustAttachAtleastSomethingToThisPost;
            }

            if (this.isCaptionEditorNeeded() && topic.isMediaCaptionRequired()) {
                var captionsAreValid = this.attachments().length === 0 || this.attachments().every(a => a.title().length > 0);

                if (!captionsAreValid)
                    return messages.AttachmentShouldHaveTitle;
            }
        } else {
            return messages.InvalidRequest;
        }
    }

    private checkIfHasMedia() {
        return this.attachments().length > 0 ||
            TextHelpers.checkIfHasAllowedMedia(this.text(), { images: this.imagesAreAllowed(), video: this.videoIsAllowed() });
    }

    private checkIfTitleEditorNeeded() {
        return this.textIsAllowed() && this.titleAllowed();
    }

    private checkIfCaptionEditorNeeded() {
        var topic = this.topic(),
            threadOwner = this.threadOwner();

        if (topic != undefined && threadOwner != undefined) {
            if (!threadOwner.isRegular())
                return false;

            if (topic.mediaCaptionType() === enums.ThreadMediaCaptionTypes.Nothing.value)
                return false;

            if (topic.isVoxPop())
                return this.isReplyEditor() && topic.participantsReplyType() !== enums.ParticipantsReplyTypes.OnlyVideo.value;

            return topic.hasSpecialCommentForm() ? this.isReplyEditor() : !this.isReplyEditor();
        }

        return false;
    }    

    private checkIfVoxPopMessageNeeded() {
        var topic = this.topic(),
            threadOwner = this.threadOwner();

        if (topic != undefined && threadOwner != undefined) {
            return topic.isVoxPop() &&
                threadOwner.isRegular() &&
                this.parentThreadId() == undefined &&
                topic.participantsCommentType() !== enums.ParticipantsCommentTypes.OnlyVideo.value &&
                !this.isForcedTextMode() &&
                this.uploads().length === 0 &&
                this.attachments().length === 0;
        }

        return false;
    }
}